import { api, baseAxios } from "../../../api/api";
import {
  getContentType,
  getFileUploadWarningMessage,
  shouldCompressFile,
  shouldConvertFile,
} from "../../FileUploadIcon/helpers";

interface IUploadFilePayload {
  file: File | null;
  module_name: string;
  defaultReturn: string;
  asPayload: {
    [key: string]: string | number | null;
  };
  onWarning?: (message: string) => void;
}

export const uploadFile = async ({
  file,
  module_name,
  defaultReturn,
  asPayload,
  onWarning,
}: IUploadFilePayload) => {
  if (file) {
    const formdata = new FormData();

    const isConvertTrue = shouldConvertFile(file);
    const isCompressedTrue = shouldCompressFile(file);

    const warningMessage = getFileUploadWarningMessage(
      isConvertTrue,
      isCompressedTrue
    );
    if (warningMessage && onWarning) {
      onWarning(warningMessage);
    }

    const options = {
      convert: isConvertTrue,
      compressed: isCompressedTrue,
    };

    const contentType = getContentType(file);

    const file_metadata = {
      ContentType: contentType.toLowerCase(),
      ContentDisposition: "attachment",
    };

    formdata.append("files", file);
    formdata.append("module_name", module_name);
    formdata.append("as_payload", JSON.stringify(asPayload));
    formdata.append("options", JSON.stringify(options));
    formdata.append("file_metadata", JSON.stringify(file_metadata));

    const res = await api.post("/general/upload-files", formdata);
    const data: string[] = res.data.data;
    if (data.length > 0) {
      return data[0];
    }
  }
  return defaultReturn;
};

interface IUploadOnboardingFilePayload {
  file: File | null;
  module_name: string;
  defaultReturn: string;
  asPayload: {
    [key: string]: string | number | null;
  };
  headers?: {
    [key: string]: string;
  };
  onWarning?: (message: string) => void;
}
export const uploadOnboardingFile = async ({
  file,
  module_name,
  defaultReturn,
  asPayload,
  headers,
  onWarning,
}: IUploadOnboardingFilePayload) => {
  if (file) {
    const formdata = new FormData();

    const isConvertTrue = shouldConvertFile(file);
    const isCompressedTrue = shouldCompressFile(file);

    const warningMessage = getFileUploadWarningMessage(
      isConvertTrue,
      isCompressedTrue
    );
    if (warningMessage && onWarning) {
      onWarning(warningMessage);
    }

    const options = {
      convert: isConvertTrue,
      compressed: isCompressedTrue,
    };

    const contentType = getContentType(file);

    const file_metadata = {
      ContentType: contentType.toLowerCase(),
      ContentDisposition: "attachment",
    };

    formdata.append("files", file);
    formdata.append("module_name", module_name);
    formdata.append("as_payload", JSON.stringify(asPayload));
    formdata.append("options", JSON.stringify(options));
    formdata.append("file_metadata", JSON.stringify(file_metadata));

    const res = await api.post("/general/upload-files-onbarding", formdata, {
      headers: headers || {},
    });
    const data: string[] = res.data.data;
    if (data.length > 0) {
      return data[0];
    }
  }
  return defaultReturn;
};


export const UseruploadOnboardingFile = async ({
  file,
  module_name,
  defaultReturn,
  asPayload,
  headers,
  onWarning,
}: IUploadOnboardingFilePayload) => {
  if (file) {
    // Fetch ENR auth key (same as UserOnboard flow)
    let enrAuthKey: string | undefined;
    try {
      const envRes = await api.get(
        `/siteConfiguration/get-environment-configuration?pageNo=1&itemPerPage=500&from_date=&to_date=`,
      );
      const envList = (envRes?.data?.data || []) as Array<{
        env_key?: string;
        env_value?: string;
      }>;
      enrAuthKey = envList.find((x) => x.env_key === "ENR_AUTH_KEY")?.env_value;
    } catch {
      // Best-effort: continue upload even if env key fetch fails
    }

    const formdata = new FormData();

    const isConvertTrue = shouldConvertFile(file);
    const isCompressedTrue = shouldCompressFile(file);

    const warningMessage = getFileUploadWarningMessage(
      isConvertTrue,
      isCompressedTrue
    );
    if (warningMessage && onWarning) {
      onWarning(warningMessage);
    }

    const options = {
      convert: isConvertTrue,
      compressed: isCompressedTrue,
    };

    const contentType = getContentType(file);

    const file_metadata = {
      ContentType: contentType.toLowerCase(),
      ContentDisposition: "attachment",
    };

    formdata.append("files", file);
    formdata.append("module_name", module_name);
    // Send auth-key in payload (and drop any auth-token fields)
    const anyPayload = asPayload as Record<string, unknown>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ["auth-token"]: _authToken, ["auth-Token"]: _authToken2, ...rest } =
      anyPayload;
    const payloadWithAuthKey = {
      ...rest,
      "auth-key": enrAuthKey ?? null,
    };
    formdata.append("as_payload", JSON.stringify(payloadWithAuthKey));
    formdata.append("options", JSON.stringify(options));
    formdata.append("file_metadata", JSON.stringify(file_metadata));

    const filteredHeaders = Object.fromEntries(
      Object.entries(headers || {}).filter(
        ([k]) => !["auth-token", "auth-Token"].includes(k),
      ),
    );

    // Send auth-key in headers for upload request (normalize to a single key)
    const existingAuthKey =
      filteredHeaders["auth-key"] ?? filteredHeaders["auth-Key"] ?? enrAuthKey;
    if (existingAuthKey) {
      filteredHeaders["auth-key"] = existingAuthKey;
    }
    delete filteredHeaders["auth-Key"];

    const res = await baseAxios.post("/general/webhook/upload-files", formdata, {
      headers: filteredHeaders,
    });
    const data: string[] = res.data.data;
    if (data.length > 0) {
      return data[0];
    }
  }
  return defaultReturn;
};
